"use client";

import React from "react";
import { PhoneCall, Clock, MapPin, FileCheck, TriangleAlert as AlertTriangle, Truck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const FAQ_ITEMS = [{
  id: "item-1",
  question: "What is your emergency response time and deployment for Howard County?",
  answer: "Our mobile crews typically arrive within approximately 60 minutes across Howard County. By staging service vehicles close to primary thoroughfares including the Route 29 and I-95 corridors, we dispatch field specialists directly to your property with professional-grade extraction, drying, and containment equipment ready to deploy on arrival.",
  icon: Clock
}, {
  id: "item-2",
  question: "Which communities and areas of Howard County does TriServe serve?",
  answer: "We provide rapid emergency restoration coverage throughout all of Howard County. This includes all Columbia planned villages (Wilde Lake, Harper's Choice, Long Reach, Oakland Mills, Kings Contrivance, River Hill, Owen Brown, Hickory Ridge, Town Center, and Dorsey's Search), historic flood-prone zones in Ellicott City, as well as Clarksville, Elkridge, Jessup, Dayton, Fulton, Glenelg, and Highland.",
  icon: MapPin
}, {
  id: "item-3",
  question: "How does TriServe coordinate with insurance adjusters and claim processing?",
  answer: "We work directly alongside your property insurance carrier and claims adjuster from day one. Our technicians record thermal moisture logs, room-by-room digital imaging, and itemized scope documentation using standard industry estimating platforms so your claim has comprehensive, objective loss verification with minimal delay.",
  icon: FileCheck
}, {
  id: "item-4",
  question: "What immediate steps should a resident take after water damage, fire, or mold is discovered?",
  answer: "First, prioritize personal safety: avoid entering areas with standing water near electrical panels or submerged wiring. If safe and accessible, shut off the main water valve to prevent further flooding, or turn off the HVAC system to avoid spreading airborne contaminants during smoke or mold events. Call professional emergency mitigation immediately to prevent secondary structural deterioration.",
  icon: AlertTriangle
}, {
  id: "item-5",
  question: "Does TriServe have a physical walk-in customer office in Howard County?",
  answer: "No. TriServe operates solely as a mobile-dispatched disaster restoration service across Maryland. We maintain no public walk-in facility; our fully equipped fleet dispatches directly to residential and commercial properties 24/7 to begin remediation without office delays.",
  icon: Truck
}, {
  id: "item-6",
  question: "How can I access 24/7 emergency restoration dispatch in Howard County?",
  answer: "You can reach our emergency dispatch team 24 hours a day, 365 days a year by calling 855-435-6631. You will connect directly with our local restoration staff—never an impersonal third-party call center—who can initiate immediate mobile dispatch to your Howard County address.",
  icon: PhoneCall
}];
export default function HowardCountyFaq() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ITEMS.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
  return <section id="howardcountyrestorationfaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Badge variant="outline" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/30 bg-primary/5">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            24/7 Howard County Mobile Dispatch
          </Badge>

          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Howard County Restoration FAQs
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Essential information regarding local mobile response times, service zones, insurance collaboration, and emergency protocols across Howard County.
          </p>
        </div>

        {/* Accordion Questions */}
        <div className="bg-card text-card-foreground rounded-lg border border-border shadow-xs p-2 sm:p-6 mb-10">
          <Accordion type="single" collapsible className="w-full space-y-2">
            {FAQ_ITEMS.map((item, index) => {
            const IconComponent = item.icon;
            return <AccordionItem key={item.id} value={item.id} data-index={index} className="border border-border/80 rounded-md px-4 py-1 data-[state=open]:bg-muted/40 transition-colors">
                  <AccordionTrigger className="hover:no-underline py-4 text-base font-semibold text-foreground text-left gap-4">
                    <span className="flex items-center gap-3">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <IconComponent className="size-4" />
                      </span>
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-10 pr-2 pt-1 pb-4">
                    {item.answer}
                    {item.id === "item-6" && <div className="mt-4 pt-2">
                        <Button asChild variant="default" size="sm" className="font-medium bg-primary text-primary-foreground hover:bg-primary/90">
                          <a href="tel:8554356631" className="inline-flex items-center gap-2">
                            <PhoneCall className="size-4" />
                            <span>Call 855-435-6631 Now</span>
                          </a>
                        </Button>
                      </div>}
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </div>

        {/* Emergency Dispatch Assistance Bar */}
        <div className="rounded-lg border border-border bg-muted/50 p-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h3 className="text-base font-semibold text-foreground">
              Experiencing active water, fire, or mold damage in Howard County?
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Local technicians dispatch directly to your door in approximately 60 minutes.
            </p>
          </div>
          <Button asChild variant="default" size="lg" className="shrink-0 bg-primary text-primary-foreground shadow-xs hover:bg-primary/90">
            <a href="tel:8554356631" className="flex items-center gap-2">
              <PhoneCall className="size-4" />
              <span>855-435-6631</span>
            </a>
          </Button>
        </div>
      </div>

      {/* Structured Data: FAQPage Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
    </section>;
}